import React, { useEffect, useRef, useState } from 'react';
import LottieView from 'lottie-react-native';
import { Animated, Platform, View } from 'react-native';

import NyanCat from '../../assets/images/nyan-cat.png';
import { isCanvas } from '@common/utils';

const RAINBOW_COLORS = [
  '#F70000',
  '#F69400',
  '#F7F700',
  '#32F602',
  '#0094F6',
  '#6231F6',
];

type SizeProp = {
  width: number;
  height: number;
};

const ProgressBarNyanCat: React.FC<{
  progress: number;
  total: number;
  barSize: SizeProp;
  catSize: SizeProp;
  backgroundColor: string;
}> = ({ progress, total, barSize, catSize, backgroundColor }) => {
  const animation = useRef<LottieView>(null);
  const [lottieError, setLottieError] = useState<string>();
  const positionAnim = useRef(new Animated.Value(0)).current;
  const currentProgress = progress / total;
  const canvas = isCanvas() && Platform.OS === 'web';

  useEffect(() => {
    Animated.timing(positionAnim, {
      toValue: currentProgress,
      duration: 500,
      useNativeDriver: true,
    }).start();
  }, [currentProgress]);

  useEffect(() => {
    animation.current?.reset();
    animation.current?.play();
  }, [progress]);

  return (
    <View
      style={{
        width: barSize.width,
        height: barSize.height,
        backgroundColor: backgroundColor || '#003263',
        position: 'relative',
      }}
    >
      <View style={{ width: barSize.width, overflow: 'hidden' }}>
        <Animated.View
          style={{
            width: barSize.width,
            transform: [
              {
                translateX: positionAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [barSize.width / total, barSize.width + 25],
                }),
              },
            ],
            left: -barSize.width,
          }}
        >
          {RAINBOW_COLORS.map((color) => (
            <View
              key={color}
              style={{
                width: '100%',
                height: barSize.height ? barSize.height / 6 : 3,
                backgroundColor: color,
              }}
            />
          ))}
        </Animated.View>
      </View>

      {!canvas && (
        <LottieView
          ref={animation}
          source={{
            uri: 'https://lottie.host/d836e7f6-d4f6-4905-90d9-bb03026f8b1c/FvBhT4il0i.lottie',
          }}
          webStyle={{
            position: 'absolute',
            top: -(barSize.height + 24) / 2,
            height: barSize.height + 24,
            transform: `translateX(${progress * (barSize.width / total)}px)`,
          }}
          style={{
            position: 'absolute',
            top: -(barSize.height + 24) / 2,
            height: barSize.height + 24,
            width: 100,
            transform: [
              {
                translateX: (progress - 1) * (barSize.width / total),
              },
            ],
          }}
          onAnimationFailure={(error) => {
            console.error('===Lottie Error===', error);
            setLottieError(error);
          }}
          loop={false}
        />
      )}

      <Animated.Image
        source={NyanCat}
        style={{
          transform: [
            {
              translateY: positionAnim.interpolate({
                inputRange: [0, 0.125, 0.25, 0.5, 0.75, 1],
                outputRange: [0, -6, 6, -6, 6, 0],
              }),
            },
            {
              translateX: positionAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [barSize.width / total, barSize.width + 25],
              }),
            },
          ],
          height: catSize.height || 28,
          width: catSize.width || 47,
          position: 'absolute',
          top: -6,
          left: -25,
        }}
      />
    </View>
  );
};

export default ProgressBarNyanCat;
